import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { gql, useMutation, useLazyQuery, useQuery } from '@apollo/client';
import {
  Box,
  Grid,
  Divider,
  Typography,
  Button,
  Paper,
  IconButton,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate, useParams } from 'react-router-dom';
import Dropzone, { useDropzone } from 'react-dropzone';
import PdfCard from '../common/PdfCard';
import { FlexEnd } from '../../commonStyles';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar, showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import Dropdown from '../common/formControls/Dropdown';
import TextInput from '../common/formControls/TextInput';
import { convertAllDates, numToCurrency, getOutdoorSize, exportWorksheetPdf, netHST, createPdf, download } from '../../utils/Functions';
import { baseStyle, activeStyle, acceptStyle, rejectStyle, soldStatuses, libertyProjects } from '../../utils/Constants';
import { selectUser } from '../../features/auth/authSlice';
import { selectProject } from '../../features/project/projectSlice';
import { IUnit } from '../../types/unit';
import { IWorksheet, IWorksheetById } from '../../types/worksheet';
import LoadingWrapper from '../common/LoadingWrapper';
import { IDealOption } from '../../types/CreateDealForm';
import { IOption } from '../../types/project';
import { ICoopRate } from '../../types/project';
import Commission from '../createDeal/createDealSteps/realtor/Commission';
import ArchiveDialog from './ArchiveDialog';
import EditWorksheetPurchaser from './EditWorksheetPurchaser';

const ACCENT = '#2e453d';
const BORDER = '#e4e4e0';
const MUTED = '#6a6a67';

const SectionCard = ({ title, children }: { title: React.ReactNode; children: React.ReactNode }) => (
  <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: ACCENT, mb: 2, pb: 1.5, borderBottom: `1px solid ${BORDER}` }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a1a' }}>{value || '—'}</Typography>
  </Box>
);

// readOnly + worksheetId let the Unit page's Worksheets tab show a worksheet without any editing or actions
const WorksheetDetails = ({ worksheetId, readOnly = false }: { worksheetId?: string; readOnly?: boolean }) => {
  const storeDispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const project = useSelector(selectProject);
  const navigate = useNavigate();
  const params = useParams();
  const worksheetid = worksheetId || params.worksheetid;
  const [selectedWorksheet, setSelectedWorksheet] = useState<IWorksheet | null>(null);
  const [worksheets, setWorksheets] = useState<IWorksheet[]>([]);
  const [previousUnit, setPreviousUnit] = useState<IUnit | null>(null);
  const [availableUnits, setAvailableUnits] = useState<IUnit[]>([]);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState<boolean>(false);
  const [editingPurchaser, setEditingPurchaser] = useState<number | null>(null);

  const displayPrice = (price: number) => numToCurrency.format(project.netHst ? netHST(price, project) : price);
  const [coopRates, setCoopRates] = useState<ICoopRate[]>(selectedWorksheet ? selectedWorksheet.coop : []);

  const { loading } = useQuery<IWorksheetById>(GETWORKSHEET, {
    variables: { _id: worksheetid },
    onCompleted: (data) => {
      setCoopRates(data.worksheetById.coop);
      setSelectedWorksheet(data.worksheetById);
    },
  });

  useQuery<IWorksheetById>(GETWORKSHEET, {
    variables: { search: '', project: project._id, unit: selectedWorksheet?.unit, page: 1, perPage: 10000 },
    onCompleted: (data) => {},
  });

  const [getWorksheetTemplate, { loading: developerWorksheetLoading }] = useLazyQuery(WORKSHEETTEMPLATE, { fetchPolicy: 'network-only' });

  // Fills the project's "Worksheet" merge template with this worksheet's details (the developer's own worksheet form)
  const downloadOfferWorksheet = async () => {
    if (!selectedWorksheet) return;
    const { data } = await getWorksheetTemplate({ variables: { filter: { project: project._id, name: 'Worksheet' } } });
    const template = data?.mergeTemplateMany?.[0];
    if (!template || !template.getUrl) {
      storeDispatch(showErrorSnackbar('There is no "Worksheet" merge template for this project'));
      return;
    }

    try {
      // Merge price fields read unit.basePrice, so use the price submitted on the worksheet
      const unit = selectedWorksheet.unit
        ? { ...selectedWorksheet.unit, basePrice: selectedWorksheet.worksheetBasePrice || selectedWorksheet.unit.basePrice }
        : {};
      // Deposits come from the worksheet's deal (none until a deal has been created from it)
      const deposits = (selectedWorksheet.deal as any)?.deposit || [];
      const realtor = selectedWorksheet.realtor ? { ...selectedWorksheet.realtor, coopRates } : null;

      const file = await createPdf(
        template,
        project,
        unit,
        selectedWorksheet.purchasers,
        [],
        [],
        deposits,
        selectedWorksheet.options,
        realtor,
        null,
        null,
        null,
        // There's no deal yet, so stand in an empty one: the merge fields read these deal arrays/fields directly,
        // and the WorksheetChoice fields read the worksheet from deal.worksheet
        {
          worksheet: selectedWorksheet,
          adjustments: [],
          debit: [],
          thirdParty: [],
          upgradeSetOne: null,
          upgradeSetTwo: null,
          upgradeSetThree: null,
          salesRep: selectedWorksheet.salesRep || null,
        }
      );
      const suite = selectedWorksheet.unit ? ` - Suite ${selectedWorksheet.unit.suite}` : '';
      download(file, `${project.name}${suite} - Offer Worksheet.pdf`, 'application/pdf');
    } catch (err) {
      console.log(err, 'err');
      storeDispatch(showErrorSnackbar('Unable to create the Offer Worksheet'));
    }
  };

  const [getUnits] = useLazyQuery(UNITS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      // Sorted by suite number (numeric-aware, so 202 comes before 1001)
      setAvailableUnits(
        [...data.unitMany].sort((a: IUnit, b: IUnit) => `${a.suite}`.localeCompare(`${b.suite}`, undefined, { numeric: true }))
      );
    },
  });

  const [uploadCheque] = useMutation(UPLOADCHEQUE, {
    onCompleted: (data) => {},
    onError: (error) => {
      console.log(error);
    },
  });

  const [updateArchive] = useMutation(UPDATEWORKSHEET, {
    onCompleted: (data) => {
      if (data.worksheetUpdateById.record.status === 'archived') {
        storeDispatch(showSuccessSnackbar(`Worksheet has been archived`));
      } else {
        storeDispatch(showSuccessSnackbar(`Worksheet has been restored`));
      }
      navigate(-1);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateUnit] = useMutation(UPDATEUNIT, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateWorksheet] = useMutation(UPDATEWORKSHEET, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  useEffect(() => {
    if (selectedWorksheet?.realtor) {
      getUnits({
        variables: {
          filter: {
            project: project._id,
            OR: [
              { status: 'SHL' },
              { status: 'HL' },
              { status: 'HL2' },
              { status: 'HL3' },
              { status: 'A' },
              { status: 'CH' },
              { status: 'M' },
              { status: 'PL' },
              {
                AND: [
                  {
                    allocation: selectedWorksheet?.realtor._id,
                  },
                  { status: 'D' },
                ],
              },
            ],
          },
        },
      });
    } else {
      getUnits({
        variables: {
          filter: {
            project: project._id,
            OR: [
              { status: 'SHL' },
              { status: 'HL' },
              { status: 'HL2' },
              { status: 'HL3' },
              { status: 'A' },
              { status: 'CH' },
              { status: 'M' },
              { status: 'PL' },
            ],
          },
        },
      });
    }
  }, [selectedWorksheet]);

  const { isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/jpeg, image/png, application/pdf',
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const handleDrop = (acceptedFiles: any) => {
    if (acceptedFiles.length === 0) {
      storeDispatch(showErrorSnackbar('This file type is not allowed'));
      return;
    }
    const file = acceptedFiles[0];
    const fileReader = new FileReader();
    if (file) {
      fileReader.readAsDataURL(file);
    }
    fileReader.onloadend = async () => {
      setSelectedWorksheet({
        ...selectedWorksheet!,
        chequeImages: [
          ...selectedWorksheet?.chequeImages!,
          {
            getUrl: fileReader.result,
            file: file,
            name: file.name,
          },
        ],
      });
    };
  };

  const handleTextInput = (event: any) => {
    if (selectedWorksheet) {
      let updateWorksheetPrice = {
        ...selectedWorksheet,
        [event.target.name]: event.target.name === 'worksheetBasePrice' ? parseFloat(event.target.value) : event.target.value,
      };
      setSelectedWorksheet(updateWorksheetPrice);
    }
  };

  const handleDelete = (numIndex: number, file: any) => {
    if (file) {
      let chequeImages = selectedWorksheet?.chequeImages!.filter((chequeImages: any, index: number) => numIndex !== index);
      setSelectedWorksheet({
        ...selectedWorksheet!,
        chequeImages: chequeImages!,
      });
    } else {
      storeDispatch(showErrorSnackbar(`Cannot Delete`));
    }
  };

  const handleUnitChange = (newUnit: IUnit | null) => {
    if (!selectedWorksheet) return;

    let updateWorksheetUnit: any = newUnit
      ? {
          ...selectedWorksheet,
          unit: newUnit,
          worksheetBasePrice: newUnit.basePrice,
          notes: `${selectedWorksheet.notes} ++ Purchaser has switched from ${
            selectedWorksheet.unit ? selectedWorksheet.unit.suite : 'Wishlist'
          } to ${newUnit.suite}. ${
            selectedWorksheet.unit ? displayPrice(selectedWorksheet.unit.basePrice) : 'Wishlist'
          } to ${displayPrice(newUnit.basePrice)}`,
        }
      : {
          ...selectedWorksheet,
          unit: null,
          notes: `Agent Notes: ${selectedWorksheet.notes} ++ Purchaser has switched from ${
            selectedWorksheet.unit ? selectedWorksheet.unit.suite : 'Wishlist'
          } to Wishlist.`,
        };

    if (selectedWorksheet.unit) {
      setPreviousUnit(selectedWorksheet.unit);
    }
    setSelectedWorksheet(updateWorksheetUnit);
  };

  const optionValue = (name: string, type: string) => {
    let filteredOption: any = selectedWorksheet?.options!.filter((option: IDealOption, index: number) => option.name === name);
    if (filteredOption.length > 0) {
      return filteredOption[0][type];
    } else {
      return 0;
    }
  };

  const handleOptionDropdown = (event: any, numIndex: number) => {
    if (selectedWorksheet) {
      let filteredOption: any = project.options.filter((option: IDealOption, index: number) => option.name === event.target.name);
      if (selectedWorksheet.options.some((option: IDealOption) => option.name === event.target.name)) {
        if (event.target.value == 0) {
          let removeOption = selectedWorksheet.options.filter((data: IDealOption, index: number) => data.name !== event.target.name);
          setSelectedWorksheet({
            ...selectedWorksheet,
            options: removeOption,
          });
        } else {
          let optionsArray = selectedWorksheet.options.map((option: IDealOption, index: number) => {
            if (option.name === event.target.name) {
              return { ...option, purchaseAmount: parseFloat(event.target.value), amount: event.target.value * filteredOption[0].price };
            } else return option;
          });
          setSelectedWorksheet({
            ...selectedWorksheet,
            options: optionsArray,
          });
        }
      } else {
        if (project.options) {
          setSelectedWorksheet({
            ...selectedWorksheet,
            options: [
              ...selectedWorksheet.options,
              {
                name: event.target.name,
                purchaseAmount: parseFloat(event.target.value),
                amount: event.target.value * filteredOption[0].price,
              },
            ],
          });
        } else return;
      }
    }
  };

  const handleOptionInput = (event: any, optionName: string) => {
    if (selectedWorksheet) {
      let optionsArray = selectedWorksheet.options.map((option: IDealOption, index: number) => {
        if (option.name === optionName) {
          return {
            ...option,
            amount: parseFloat(event.target.value),
          };
        } else {
          return option;
        }
      });
      setSelectedWorksheet({
        ...selectedWorksheet,
        options: optionsArray,
      });
    }
  };

  const showSuccess = () => {
    if (selectedWorksheet! && user.type === 'Manager') {
      if (selectedWorksheet.managerApproval && selectedWorksheet.unit._id && selectedWorksheet.status === 'accepted') {
        return 'Unapprove Worksheet';
      } else {
        if (selectedWorksheet.status === 'archived') {
          return 'Unarchive';
        } else if (!selectedWorksheet.managerApproval && selectedWorksheet.unit && user.type === 'Manager') {
          return 'Save and Approve Worksheet';
        } else return null;
      }
    } else return null;
  };

  // Buttons
  const handleCloseSuccess = () => {
    if (!selectedWorksheet?.managerApproval) {
      if (selectedWorksheet?.unit) {
        let approvedWorksheet = worksheets.find(
          (worksheet: IWorksheet) => worksheet.unit.suite === selectedWorksheet?.unit.suite && worksheet.managerApproval
        );
        if (approvedWorksheet) {
          storeDispatch(showErrorSnackbar(`Another worksheet has already been approved for this unit`));
          return;
        }
        updateWorksheet({
          variables: {
            _id: selectedWorksheet?._id,
            record: {
              unit: selectedWorksheet?.unit._id,
              managerApproval: { user: user._id },
              worksheetBasePrice: selectedWorksheet?.worksheetBasePrice,
              notes: selectedWorksheet?.notes,
              status: 'accepted',
              options: selectedWorksheet.options,
              deal: null,
              coop: selectedWorksheet.coop,
              archivedBy: null,
            },
          },
        }).then((res) => {
          if (res.data.worksheetUpdateById.record.status === 'accepted') {
            if (res.data.worksheetUpdateById.record.unit.worksheets.length === 0) {
              updateUnit({
                variables: {
                  _id: res.data.worksheetUpdateById.record.unit._id,
                  record: {
                    status: soldStatuses.includes(selectedWorksheet?.unit?.status!) ? selectedWorksheet?.unit.status : 'WA',
                    worksheets: [res.data.worksheetUpdateById.record._id],
                  },
                },
              });
            } else {
              updateUnit({ variables: { _id: res.data.worksheetUpdateById.record.unit._id, record: { status: 'WA' } } });
            }
          }
          navigate(-1);
          storeDispatch(showSuccessSnackbar('Worksheet Approved!'));
        });
      } else {
        storeDispatch(showErrorSnackbar(`Select a Unit to Approve`));
        return;
      }
    } else {
      updateWorksheet({
        variables: { _id: selectedWorksheet?._id, record: { managerApproval: null, status: 'pending', archivedBy: null } },
      }).then((res) => {
        if (res.data.worksheetUpdateById.record.status === 'pending') {
          updateUnit({
            variables: {
              _id: res.data.worksheetUpdateById.record.unit._id,
              record: { status: soldStatuses.includes(selectedWorksheet?.unit?.status!) ? selectedWorksheet?.unit.status : 'W' },
            },
          });
        }
        navigate(-1);
        storeDispatch(showSuccessSnackbar('Worksheet Unapproved!'));
      });
    }
  };

  const handleCloseSecond = () => {
    updateWorksheet({
      variables: {
        _id: selectedWorksheet?._id,
        record: {
          notes: selectedWorksheet?.notes,
          worksheetBasePrice: selectedWorksheet?.worksheetBasePrice,
          unit: selectedWorksheet?.unit ? selectedWorksheet?.unit._id : null,
          options: selectedWorksheet?.options,
          coop: coopRates,
        },
      },
    }).then((res) => {
      // Check if previous unit turning from has any worksheets. Change it to M
      if (previousUnit) {
        updateUnit({
          variables: {
            _id: previousUnit._id,
            record: {
              status: 'A',
              worksheets: previousUnit?.worksheets
                .map((worksheet: IWorksheet) => worksheet._id)
                .filter((worksheet: any) => worksheet !== selectedWorksheet?._id),
            },
          },
        });
      }
      if (selectedWorksheet?.chequeImages.length) {
        selectedWorksheet?.chequeImages.forEach(async (file: any) => {
          if (file.file) {
            await uploadCheque({ variables: { id: selectedWorksheet?._id, name: file.name, file: file.file } });
          }
        });
      }
      let updatedWorksheets = res.data.worksheetUpdateById.record.unit.worksheets.map((worksheet: any) => worksheet._id);
      updateUnit({
        variables: {
          _id: res.data.worksheetUpdateById.record.unit._id,
          record: { worksheets: [...updatedWorksheets, selectedWorksheet?._id] },
        },
      });
      storeDispatch(showSuccessSnackbar('Worksheet Updated!'));
      navigate(-1);
    });
  };

  const handleCloseThird = (reason: string) => {
    setArchiveDialogOpen(false);
    updateArchive({
      variables: {
        _id: selectedWorksheet?._id,
        record: {
          status: 'archived',
          unit: selectedWorksheet?.unit ? selectedWorksheet?.unit._id : null,
          archivedBy: user._id,
          reason,
        },
      },
    }).then((res) => {
      if (res.data.worksheetUpdateById.record.status === 'archived' && res.data.worksheetUpdateById.record.unit) {
        let removeWorksheetFromUnit = selectedWorksheet?.unit.worksheets
          .filter((worksheet: any) => worksheet._id.toString() !== selectedWorksheet._id.toString())
          .map((worksheet: any) => worksheet._id);
        if (res.data.worksheetUpdateById.record.unit.allocation && res.data.worksheetUpdateById.record.managerApproval) {
          updateUnit({
            variables: {
              _id: res.data.worksheetUpdateById.record.unit._id,
              record: {
                worksheets: removeWorksheetFromUnit,
                status: soldStatuses.includes(selectedWorksheet?.unit?.status!)
                  ? selectedWorksheet?.unit.status
                  : res.data.worksheetUpdateById.record.unit.allocation
                  ? 'D'
                  : 'A',
              },
            },
          });
        } else if (res.data.worksheetUpdateById.record.unit.allocation && !res.data.worksheetUpdateById.record.managerApproval) {
          updateUnit({
            variables: {
              _id: res.data.worksheetUpdateById.record.unit._id,
              record: {
                worksheets: removeWorksheetFromUnit,
                status: soldStatuses.includes(selectedWorksheet?.unit?.status!) ? selectedWorksheet?.unit.status : 'D',
              },
            },
          });
        } else {
          updateUnit({ variables: { _id: res.data.worksheetUpdateById.record.unit._id, record: { worksheets: removeWorksheetFromUnit } } });
        }
      }
    });
  };

  const handleCloseFourth = () => {
    if (selectedWorksheet) {
      if (selectedWorksheet.managerApproval && selectedWorksheet.status === 'accepted' && selectedWorksheet.unit) {
        updateWorksheet({
          variables: {
            _id: selectedWorksheet?._id,
            record: {
              worksheetBasePrice: selectedWorksheet?.worksheetBasePrice,
              notes: selectedWorksheet?.notes,
              options: selectedWorksheet.options,
            },
          },
        }).then((res) => {
          navigate(`/${project._id}/dashboard/create-deal/${selectedWorksheet.unit._id}/form/${selectedWorksheet._id}`);
        });
      }
    }
  };

  return loading ? (
    <LoadingWrapper title="Loading..." modal={false} />
  ) : selectedWorksheet ? (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {!readOnly ? (
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ mr: 1.5, backgroundColor: '#fff', border: `1px solid ${BORDER}`, '&:hover': { backgroundColor: '#f1efe9' } }}
          >
            <ArrowBackIcon sx={{ color: ACCENT }} />
          </IconButton>
        ) : null}
        <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' }, fontWeight: 700, color: '#1a1a1a' }}>
          {selectedWorksheet.unit ? (
            <>
              Suite {selectedWorksheet.unit.suite}
              <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 400, color: MUTED, ml: 1 }}>
                {selectedWorksheet.realtor ? `Worksheet by ${selectedWorksheet.realtor.fullName}` : 'Worksheet by Public'}
              </Typography>
            </>
          ) : (
            <>
              Wishlist Worksheet
              {selectedWorksheet.realtor ?
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 400, color: MUTED, ml: 1 }}>
                  by {selectedWorksheet.realtor.fullName}
                </Typography>
              : null}
            </>
          )}
        </Typography>
        {/* Same PDF as the Download button in the worksheet tables; also shown in the Unit page's read-only tab */}
        <Button
          sx={{ ml: 'auto', textTransform: 'none', backgroundColor: ACCENT, '&:hover': { backgroundColor: '#233530' } }}
          variant="contained"
          onClick={() => exportWorksheetPdf(selectedWorksheet, project)}
        >
          Download
        </Button>
        {/* Liberty projects use their own worksheet form (the project's "Worksheet" merge template) */}
        {libertyProjects.includes(project._id) ? (
          <Button
            sx={{ ml: 1, textTransform: 'none' }}
            variant="contained"
            disabled={developerWorksheetLoading}
            onClick={() => downloadOfferWorksheet()}
          >
            Offer Worksheet
          </Button>
        ) : null}
      </Box>
      {selectedWorksheet?.purchasers.map((purchaser: any, index: number) => (
        <SectionCard
          key={index}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Purchaser {index + 1}
              <Chip
                label={purchaser.purchaserType || 'Purchaser'}
                size="small"
                sx={{ backgroundColor: '#f1efe9', color: ACCENT, fontWeight: 700, fontSize: '0.7rem' }}
              />
              {editingPurchaser !== index && !readOnly ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={editingPurchaser !== null}
                  onClick={() => setEditingPurchaser(index)}
                  sx={{ ml: 'auto', textTransform: 'none', borderRadius: '8px' }}
                >
                  Edit
                </Button>
              ) : null}
            </Box>
          }
        >
          {editingPurchaser === index ? (
            <EditWorksheetPurchaser
              purchaserId={purchaser._id}
              onCancel={() => setEditingPurchaser(null)}
              onSaved={(saved) => {
                setSelectedWorksheet({
                  ...selectedWorksheet,
                  purchasers: selectedWorksheet.purchasers.map((current: any, currentIndex: number) =>
                    currentIndex === index ? { ...current, ...saved } : current
                  ),
                });
                setEditingPurchaser(null);
              }}
            />
          ) : (
          <>
          {purchaser.identifications.length > 0 ? (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {purchaser.identifications.map((attachment: any, idIndex: number) => (
                <Grid key={idIndex} item md={4} sm={6} xs={12}>
                  <PdfCard
                    file={attachment.getUrl ? attachment.getUrl : attachment.url}
                    title={attachment.name ? attachment.name : attachment.file.name}
                    id={idIndex}
                    handleDelete={readOnly ? undefined : () => storeDispatch(showErrorSnackbar(`Cannot Delete`))}
                    download={true}
                    index={idIndex}
                  />
                </Grid>
              ))}
            </Grid>
          ) : null}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow label="Full Name" value={`${purchaser.firstName} ${purchaser.lastName}`} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow
                label="Address"
                value={`${purchaser.streetAddress}, ${purchaser.city}, ${purchaser.province}, ${purchaser.postalCode}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow label="SIN" value={purchaser.sin || 'N/A'} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow label="Date of Birth" value={convertAllDates(purchaser.dob, 'PPP')} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow label="Phone" value={purchaser.primaryPhone} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow label="Email" value={purchaser.email} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow label="ID Type" value={purchaser.idType} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow label="ID Jurisdiction" value={purchaser.idJurisdiction} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow label="Occupation" value={purchaser.occupation} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoRow label="Employer" value={purchaser.employer} />
            </Grid>
          </Grid>
          </>
          )}
        </SectionCard>
      ))}
      <SectionCard title="Bank Draft / Cheque Image">
        {readOnly ? (
          selectedWorksheet.chequeImages.length ? null : (
            <Typography sx={{ fontSize: '0.85rem', color: MUTED }}>No bank drafts or cheques uploaded</Typography>
          )
        ) : (
        <Dropzone onDrop={(files) => handleDrop(files)} accept="image/jpg, image/jpeg, image/png, application/pdf">
          {({ getRootProps, getInputProps }) => (
            <section>
              <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <UploadFileIcon sx={{ color: ACCENT, fontSize: '1.8rem' }} />
                  <Typography sx={{ fontSize: '0.85rem', color: '#1a1a1a', fontWeight: 600 }}>Drag and Drop or Upload Images</Typography>
                </Box>
              </div>
            </section>
          )}
        </Dropzone>
        )}
        {selectedWorksheet.chequeImages.length > 0 ? (
          <div>
            {!readOnly ? <Divider sx={{ my: 2 }} /> : null}
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a', mb: 1 }}>Bank Drafts / Cheques</Typography>
            <Grid container spacing={2}>
              {selectedWorksheet.chequeImages.map((attachment: any, index: number) => {
                return (
                  <Grid sx={{ mt: 2 }} key={index} item md={3} xs={12}>
                    <PdfCard
                      file={attachment.getUrl ? attachment.getUrl : attachment.url}
                      title={attachment.name ? attachment.name : attachment.file.name}
                      id={index}
                      handleDelete={readOnly ? undefined : () => handleDelete(index, attachment.file!)}
                      download={true}
                      index={index}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </div>
        ) : null}
      </SectionCard>
      <SectionCard title="Option Information">
        <Grid container spacing={2}>
          {project.options
            .filter((option: any) => {
              if (selectedWorksheet.unit) {
                return option.type === selectedWorksheet.unit.type;
              } else return option.type === 'residential';
            })
            .map((option: IOption, index: number) => {
              return (
                <React.Fragment key={index}>
                  <Grid sx={{ alignSelf: 'center' }} item xs={12} sm={3}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>{option.name}</Typography>
                  </Grid>
                  {readOnly ? (
                    <>
                      <Grid item xs={12} sm={5}>
                        <InfoRow label="Quantity" value={String(optionValue(option.name, 'purchaseAmount') || 0)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InfoRow label="Total Amount" value={numToCurrency.format(Number(optionValue(option.name, 'amount')) || 0)} />
                      </Grid>
                    </>
                  ) : (
                  <>
                  <Grid item xs={12} sm={5}>
                    <Dropdown
                      title={`Quantity`}
                      id={'purchaseAmount'}
                      menuList={['0', '1', '2', '3']}
                      name={option.name}
                      handleSelect={(e: any) => handleOptionDropdown(e, index)}
                      value={optionValue(option.name, 'purchaseAmount')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextInput
                      title={`${option.name} Total Amount`}
                      type="number"
                      id={'totalAmount'}
                      handleTextInput={(e: any) => handleOptionInput(e, option.name)}
                      value={optionValue(option.name, 'amount')}
                      min={'0'}
                      adornment={'$'}
                      required={false}
                    />
                  </Grid>
                  </>
                  )}
                </React.Fragment>
              );
            })}
        </Grid>
      </SectionCard>
      <SectionCard title="Commission">
        {readOnly ? (
          coopRates?.length ? (
            coopRates.map((rate: ICoopRate, index: number) => (
              <InfoRow
                key={index}
                label={rate.description || rate.type}
                value={[
                  rate.percentage ? `${rate.percentage}%` : null,
                  rate.fixedAmount ? numToCurrency.format(Number(rate.fixedAmount)) : null,
                  rate.date ? `on ${convertAllDates(rate.date, 'PP')}` : rate.days ? `${rate.days} days` : null,
                ]
                  .filter(Boolean)
                  .join(' - ')}
              />
            ))
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: MUTED }}>No commission entered</Typography>
          )
        ) : project.coopStructures?.length ? (
          <Commission coopRates={coopRates} setCoopRates={setCoopRates} />
        ) : (
          <Typography sx={{ fontSize: '0.85rem', color: MUTED }}>No Coop Structure Created</Typography>
        )}
      </SectionCard>
      <SectionCard title="Unit Information">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {selectedWorksheet.unit || user.type === 'Manager' ? (
              <Box>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
                  Switch to Suite
                </Typography>
                {!selectedWorksheet.deal && !readOnly ? (
                  <Autocomplete
                    options={availableUnits}
                    getOptionLabel={(option: IUnit) => `${option.suite} - ${option.status}`}
                    isOptionEqualToValue={(option: IUnit, value: IUnit) => option._id === value._id}
                    value={selectedWorksheet.unit || null}
                    onChange={(_, newValue) => handleUnitChange(newValue)}
                    renderInput={(params) => <TextField {...params} size="small" placeholder="Select a suite" />}
                  />
                ) : (
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>{selectedWorksheet.unit?.suite || '—'}</Typography>
                )}
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: MUTED }}>
                You do not have permission to assign this wishlist to a unit
              </Typography>
            )}
          </Grid>
          {selectedWorksheet.unit && !selectedWorksheet.wishlist ? (
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
                Worksheet Submitted Price
              </Typography>
              {!selectedWorksheet.deal && !readOnly ? (
                <TextInput
                  title={''}
                  name={'worksheetBasePrice'}
                  handleTextInput={(e: any) => handleTextInput(e)}
                  value={selectedWorksheet.worksheetBasePrice}
                  adornment={'$'}
                />
              ) : (
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  {displayPrice(selectedWorksheet.worksheetBasePrice)}
                </Typography>
              )}
            </Grid>
          ) : null}
        </Grid>
        {selectedWorksheet.unit ? (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <InfoRow label="Level" value={selectedWorksheet.unit.level} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoRow label="Unit Number" value={selectedWorksheet.unit.unit} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoRow label="Unit Type" value={selectedWorksheet.unit.unitType} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoRow label="Model Type" value={selectedWorksheet.unit.modelType} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoRow label="Bathroom" value={selectedWorksheet.unit.bathroom} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoRow label="Size" value={selectedWorksheet.unit.size} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoRow label="Outdoor Size" value={getOutdoorSize(selectedWorksheet.unit.outdoor) || '0'} />
            </Grid>
          </Grid>
        ) : selectedWorksheet.wishlistChoices?.length ? (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
              Wishlist Options
            </Typography>
            {selectedWorksheet.wishlistChoices.map((choice: any, index: number) => (
              <Box
                key={index}
                sx={{ p: 1.5, mb: 1.5, borderRadius: '8px', backgroundColor: '#f1efe9', '&:last-child': { mb: 0 } }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoRow label="Unit Type" value={choice.unitType} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoRow label="Level" value={choice.level} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoRow label="Exposure" value={choice.exposure} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoRow label="Model Types" value={choice.modelTypes?.length ? choice.modelTypes.join(', ') : undefined} />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        ) : null}
      </SectionCard>
      <SectionCard title="Pricing Information">
        {selectedWorksheet.unit ? (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={selectedWorksheet.wishlist ? 12 : 6}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '8px',
                  backgroundColor: selectedWorksheet.unit.basePrice !== selectedWorksheet.worksheetBasePrice ? '#fbe4e4' : '#e1f0e1',
                }}
              >
                <InfoRow label="Current Unit Price" value={displayPrice(selectedWorksheet.unit.basePrice)} />
              </Box>
            </Grid>
            {!selectedWorksheet.wishlist ? (
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    backgroundColor: selectedWorksheet.unit.basePrice !== selectedWorksheet.worksheetBasePrice ? '#fbe4e4' : '#e1f0e1',
                  }}
                >
                  <InfoRow label="Worksheet Submitted Price" value={displayPrice(selectedWorksheet.worksheetBasePrice)} />
                </Box>
              </Grid>
            ) : null}
          </Grid>
        ) : (
          <Typography sx={{ mb: 2, color: MUTED, fontSize: '0.85rem' }}>No unit has been assigned to this worksheet</Typography>
        )}
        <Box>
          {selectedWorksheet.unit ? <InfoRow label="Price" value={displayPrice(selectedWorksheet.worksheetBasePrice)} /> : null}
          {selectedWorksheet.options
            .filter((option: IDealOption) => option.purchaseAmount)
            .map((option: IDealOption, index: number) => (
              <InfoRow key={index} label={option.name} value={`x${option.purchaseAmount} at ${numToCurrency.format(option.amount!)}`} />
            ))}
        </Box>
        {selectedWorksheet.unit ? (
          <Box sx={{ mt: 1, p: 1.5, borderRadius: '8px', backgroundColor: '#f1efe9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a1a' }}>Total Price</Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: ACCENT }}>
              {numToCurrency.format(
                selectedWorksheet.options.reduce(
                  (a: any, b: any) => a + b.amount,
                  project.netHst ? netHST(selectedWorksheet.worksheetBasePrice, project) : selectedWorksheet.worksheetBasePrice
                )
              )}
            </Typography>
          </Box>
        ) : null}
      </SectionCard>
      <SectionCard title="Worksheet Notes">
        {readOnly ? (
          <Typography sx={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', color: selectedWorksheet.notes ? '#1a1a1a' : MUTED }}>
            {selectedWorksheet.notes || 'No notes'}
          </Typography>
        ) : (
        <TextInput
          name="notes"
          id="comments"
          multiline
          variant={'outlined'}
          rows={4}
          required={true}
          value={selectedWorksheet.notes}
          rowsMax={Infinity}
          handleTextInput={(e: any) => handleTextInput(e)}
        />
        )}
      </SectionCard>
      {!readOnly ? (
      <>
      <FlexEnd sx={{ gap: 1.5, flexWrap: 'wrap' }}>
        {selectedWorksheet?.deal && selectedWorksheet.status === 'accepted' ? null : (
          <Button onClick={() => setArchiveDialogOpen(true)} variant="outlined" color="error" sx={{ textTransform: 'none', borderRadius: '8px' }}>
            Archive
          </Button>
        )}
        {!selectedWorksheet?.deal ? (
          <Button onClick={() => handleCloseSecond()} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }}>
            Save Changes
          </Button>
        ) : null}
        {selectedWorksheet?.unit && user.type === 'Manager' && !soldStatuses.includes(selectedWorksheet.unit.status) ? (
          <Button onClick={() => handleCloseSuccess()} variant="contained" sx={{ textTransform: 'none', borderRadius: '8px' }}>
            {showSuccess()}
          </Button>
        ) : null}
        {selectedWorksheet?.managerApproval && selectedWorksheet?.unit._id && selectedWorksheet.status === 'accepted' ? (
          <Button onClick={() => handleCloseFourth()} variant="contained" color="success" sx={{ textTransform: 'none', borderRadius: '8px' }}>
            Create Deal
          </Button>
        ) : null}
      </FlexEnd>
      <ArchiveDialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)} onConfirm={handleCloseThird} />
      </>
      ) : null}
    </Box>
  ) : null;
};

const UNITS = gql`
  query unitMany($filter: FilterFindManyUnitInput) {
    unitMany(filter: $filter, limit: 10000) {
      _id
      suite
      basePrice
      unit
      level
      modelType
      unitType
      exposure
      bathroom
      size
      status
      worksheets {
        _id
      }
      allocation {
        _id
        fullName
      }
      type
    }
  }
`;

const WORKSHEETTEMPLATE = gql`
  query mergeTemplateMany($filter: FilterFindManyMergeTemplateInput) {
    mergeTemplateMany(filter: $filter, limit: 1) {
      _id
      name
      mergeFields {
        key
        index
        pageNumber
        x
        y
        fontSize
        format
        wrap
      }
      totalPages
      getUrl
    }
  }
`;

const GETWORKSHEET = gql`
  query worksheetById($_id: MongoID!) {
    worksheetById(_id: $_id) {
      _id
      project {
        _id
      }
      deal {
        _id
        createdAt
        deposit {
          _id
          name
          amount
          dueDate
          chequeType
        }
      }
      status
      unit {
        _id
        suite
        basePrice
        unit
        level
        modelType
        unitType
        exposure
        bathroom
        size
        status
        worksheets {
          _id
        }
        allocation {
          _id
          fullName
        }
        type
      }
      salesRep {
        fullName
      }
      realtor {
        _id
        firstName
        lastName
        fullName
        email
        directPhone
        brokerage
        brokeragePhone
        brokerageFax
        streetAddress
        city
        province
        postalCode
      }
      coop {
        type
        days
        date
        description
        percentage
        fixedAmount
      }
      purchasers {
        _id
        project {
          _id
        }
        firstName
        lastName
        email
        streetAddress
        city
        province
        country
        postalCode
        purchaserType
        employer
        occupation
        primaryPhone
        dob
        sin
        idType
        unit
        idJurisdiction
        identifications {
          name
          getUrl
        }
      }
      wishlist
      joinWaitlist
      wishlistChoices {
        modelTypes
        unitType
        level
        exposure
      }
      options {
        name
        amount
        purchaseAmount
        rent
      }
      notes
      managerApproval {
        user {
          fullName
        }
        timestamp
      }
      chequeImages {
        name
        _id
        getUrl
      }
      deposits {
        name
        type
        amount
        dateDue
      }
      totalPrice
      reason
      worksheetBasePrice
      notes
      createdAt
    }
  }
`;

const UPDATEWORKSHEET = gql`
  mutation worksheetUpdateById($_id: MongoID!, $record: UpdateByIdWorksheetInput!) {
    worksheetUpdateById(_id: $_id, record: $record) {
      record {
        _id
        project {
          _id
        }
        status
        unit {
          _id
          suite
          basePrice
          unit
          level
          modelType
          unitType
          bathroom
          size
          status
          worksheets {
            _id
          }
          allocation {
            _id
            fullName
          }
        }
        salesRep {
          fullName
        }
        realtor {
          _id
          fullName
          directPhone
          email
        }
        coop {
          type
          days
          date
          description
          percentage
          fixedAmount
        }
        purchasers {
          _id
          project {
            _id
          }
          firstName
          lastName
          email
          streetAddress
          city
          province
          country
          postalCode
          purchaserType
          employer
          occupation
          primaryPhone
          dob
          sin
          idType
          unit
          idJurisdiction
          identifications {
            name
            getUrl
          }
        }
        wishlist
        joinWaitlist
        wishlistChoices {
          modelTypes
          unitType
          level
          exposure
        }
        options {
          name
          amount
          purchaseAmount
        }
        notes
        managerApproval {
          user {
            fullName
          }
          timestamp
        }
        chequeImages {
          name
          _id
          getUrl
        }
        totalPrice
        reason
        worksheetBasePrice
        notes
        createdAt
      }
    }
  }
`;

const UPDATEUNIT = gql`
  mutation unitUpdateById($_id: MongoID!, $record: UpdateByIdUnitInput!) {
    unitUpdateById(_id: $_id, record: $record) {
      record {
        _id
        status
      }
    }
  }
`;

const UPLOADCHEQUE = gql`
  mutation uploadCheque($id: MongoID!, $name: String!, $file: Upload!) {
    uploadCheque(id: $id, name: $name, file: $file) {
      chequeImages {
        _id
        name
        getUrl
      }
    }
  }
`;

export default WorksheetDetails;
